import React, { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useAuth } from "../taskpane/AuthContext";
import { useAuthDialog } from "../taskpane/components/auth/useAuthDialog";
import { ApiClient, AuthError, createApiClient } from "./ApiClient";

interface PendingRetry {
  retry: () => void;
  reject: (reason?: unknown) => void;
}

interface ApiContextValue {
  api: ApiClient;
  needsReauth: boolean;
  reauthenticate: () => Promise<void>;
}

const ApiContext = createContext<ApiContextValue | null>(null);

const normalizeAuthError = (error: unknown): AuthError => {
  const authError = error instanceof Error ? (error as AuthError) : (new Error("Authentication failed") as AuthError);

  if (!authError.error) {
    authError.error = "login_required";
  }

  return authError;
};

export const ApiProvider = ({ children }: { children: ReactNode }) => {
  const { getAccessTokenSilently } = useAuth();
  const { openLoginDialog } = useAuthDialog();

  // Show dialog / banner when interactive login is required
  const [needsReauth, setNeedsReauth] = useState(false);

  // Queue of { retryFn, resolve, reject } for failed requests waiting on reauth
  const pendingRetriesRef = useRef<PendingRetry[]>([]);

  // Token getter. If getAccessTokenSilently throws, re-throw with a
  // normalized `.error` property so withAuthRetry can route it to the
  // reauth flow instead of letting it propagate as an unrecognised error.
  const getToken = useCallback(async () => {
    try {
      return await getAccessTokenSilently();
    } catch (err: unknown) {
      // Auth0 SDK errors already carry `.error`; for any other shape
      // (network failures, unexpected exceptions) tag it so the retry
      // logic in ApiClient recognises it as a token-acquisition failure.
      throw normalizeAuthError(err);
    }
  }, [getAccessTokenSilently]);

  // Called by createApiClient when it encounters auth error
  const handleAuthError = useCallback(<T,>(retryFn: () => Promise<T>, _error: AuthError): Promise<T> => {
    // Flag UI so we can show "session expired" screen
    setNeedsReauth(true);

    // Return a promise so we can retry this request after reauth
    return new Promise<T>((resolve, reject) => {
      pendingRetriesRef.current.push({
        retry: () => {
          retryFn().then(resolve).catch(reject);
        },
        reject,
      });
    });
  }, []);

  const api = useMemo(() => createApiClient(getToken, handleAuthError), [getToken, handleAuthError]);

  // This must be called from a real user interaction (e.g. button click)
  const reauthenticate = useCallback(async () => {
    try {
      // Pass false to prevent automatic reload - we'll handle it manually
      await openLoginDialog(false);

      setNeedsReauth(false);

      // Retry pending requests and clear queue
      const pending = pendingRetriesRef.current;
      pendingRetriesRef.current = [];
      pending.forEach(({ retry }) => retry());
    } catch (error) {
      // If reauth fails, reject pending requests
      const pending = pendingRetriesRef.current;
      pendingRetriesRef.current = [];
      pending.forEach(({ reject }) => {
        reject(error);
      });

      console.error("Reauthentication failed. Error:", error);
    }
  }, [openLoginDialog]);

  const value = useMemo<ApiContextValue>(
    () => ({
      api,
      needsReauth,
      reauthenticate,
    }),
    [api, needsReauth, reauthenticate]
  );

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

export const useApi = (): ApiContextValue => {
  const context = useContext(ApiContext);
  if (!context) throw new Error("useApi must be used within <ApiProvider>");
  return context;
};
